import { PersonaImageAvatar } from "@/components/PersonaImageAvatar.tsx";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";

export function EditablePersonaHeader({
  videoSrc,
  imageSrc,
  name,
  gender,
  buttonText = "Upload Avatar",
  onAvatarChange,
}: {
  videoSrc?: string;
  imageSrc?: string;
  name: string;
  gender?: "male" | "female";
  buttonText?: string;
  onAvatarChange:
    | null
    | ((event: ChangeEvent<HTMLInputElement>) => Promise<unknown>);
}) {
  const [uploading, setUploading] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    videoRef.current?.load();
    const video = videoRef.current;
    if (video) {
      const handleVideoEnd = () => {
        setTimeout(() => {
          if (video.paused) {
            video.play().catch((e) => console.error("Error playing video:", e));
          }
        }, 3000);
      };

      video.addEventListener("ended", handleVideoEnd);

      return () => {
        if (video) {
          video.removeEventListener("ended", handleVideoEnd);
        }
      };
    }
  }, [videoSrc]);

  return (
    <div className="w-full flex flex-col items-center mt-4">
      <div className="relative w-32 aspect-square">
        <input
          ref={photoInputRef}
          className="hidden"
          accept="image/*"
          id="avatar-upload"
          type="file"
          onChange={
            onAvatarChange
              ? async (e) => {
                  try {
                    setUploading(true);

                    await onAvatarChange(e);
                  } finally {
                    setUploading(false);
                  }
                }
              : undefined
          }
          disabled={onAvatarChange == null || uploading}
        />
        <label
          htmlFor="avatar-upload"
          className={uploading ? "cursor-wait" : "cursor-pointer"}
        >
          {videoSrc ? (
            <video
              className="w-32 h-32 object-cover rounded-full border-2 border-solid border-[#D1FA64]"
              ref={videoRef}
              loop
              autoPlay
              muted
              style={{ border: "2px solid #D1FA64" }}
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          ) : (
            <PersonaImageAvatar
              className="w-32 h-32 rounded-full border-2 border-solid border-[#D1FA64]"
              src={imageSrc}
              altText={`${name ? `${name}'s ` : ""}Avatar"`}
              gender={gender}
            />
          )}
          {uploading ? (
            <div className="w-32 h-32 absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
              <LoaderCircle className="absolute -left-4 -top-4 h-40 w-40 text-primary animate-spin stroke-1" />
            </div>
          ) : null}
        </label>
      </div>
      <Button
        onClick={() => photoInputRef.current?.click()}
        disabled={uploading}
        className="mt-2 mb-2"
      >
        {buttonText}
      </Button>
    </div>
  );
}
